import { Avatar, AvatarFallback } from '@neo/primitives/avatar'
import { Button } from '@neo/primitives/button'
import { StatusIndicator, type StatusTone } from '@neo/primitives/status-indicator'
import { Typography } from '@neo/primitives/typography'
import { Plus } from 'lucide-react'
import type { RUserSession } from 'v2/types/api/auth'
import { getAccountIdentifier } from '../utils/account-identifier'
import { IntegrationState } from '../utils/constants'
import { AccountActionsMenu, type AccountMenuAction } from './account-actions-menu'
import type { ExternalAppRow } from './external-app-actions'

const STATUS_TONE: Record<string, StatusTone> = {
  [IntegrationState.ACTIVE]: 'success',
  [IntegrationState.EXPIRED]: 'danger',
  [IntegrationState.IN_PROGRESS]: 'progress',
  [IntegrationState.DISABLED]: 'disabled',
}

const statusLabel = (state: string) => state.charAt(0).toUpperCase() + state.slice(1).toLowerCase()

export const StatusLine = ({ state }: { state: string }) => (
  <StatusIndicator tone={STATUS_TONE[state] ?? 'neutral'} data-testid="text-account-status">
    {statusLabel(state)}
  </StatusIndicator>
)

export const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography as="h2" variant="P/500" className="text-foreground">
    {children}
  </Typography>
)

const AccountAvatar = ({ label }: { label: string }) => (
  <Avatar className="h-9 w-9 flex-none">
    <AvatarFallback className="bg-bg3 text-foreground text-s-500">
      {label.trim().charAt(0).toUpperCase() || '?'}
    </AvatarFallback>
  </Avatar>
)

interface AccountsListProps {
  accounts: ExternalAppRow[]
  currentUser?: RUserSession
  onAction: (action: AccountMenuAction, connection: ExternalAppRow) => void
  /** Omitted when the viewer may not connect, or the tool is not in the catalog. */
  onAddAccount?: () => void
}

export const AccountsList = ({
  accounts,
  currentUser,
  onAction,
  onAddAccount,
}: AccountsListProps) => (
  <section className="flex flex-col gap-3" data-testid="list-connected-accounts">
    <div className="flex items-center justify-between gap-3">
      <SectionTitle>Connected accounts</SectionTitle>
      {onAddAccount && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAddAccount}
          data-testid="button-add-account"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add new account
        </Button>
      )}
    </div>
    <div>
      {accounts.map((connection) => (
        <div
          key={connection.id}
          className="flex items-center gap-3 border-border border-b py-3"
          data-testid="row-connected-account"
        >
          <AccountAvatar
            label={getAccountIdentifier(connection) || connection.integrated_by?.email || ''}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Typography variant="P/400" className="truncate text-foreground">
              {getAccountIdentifier(connection) || connection.integrated_by?.email || '-'}
            </Typography>
            {/* Status, not share count: the Share dialog already answers who has access. */}
            <StatusLine state={connection.state?.toUpperCase() ?? ''} />
          </div>
          <AccountActionsMenu
            connection={connection}
            currentUser={currentUser}
            onAction={onAction}
          />
        </div>
      ))}
    </div>
  </section>
)
